import {
  ALL_CHART_TYPES,
  BLACK_HEX,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING
} from "constants/chart.constants";
import { CalculationMethod, EvaSource, TimeStep } from "constants/charts.enums";
import { createMergeWithDefaults } from "entities/utils";
import { BoxPlotChartRequestType, BoxPlotChartSettings } from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { BASE_CHART_REQUEST } from "./defaults";

export const createDefaultBoxPlotChartSettings = (): Partial<BoxPlotChartSettings> => ({
  product: "Oil",
  chartType: ALL_CHART_TYPES.BoxPlot.id,
  otherProducts: [],
  showForecast: true,
  averageSettings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showAverage: false,
  p10Settings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showP10: false,
  p50Settings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showP50: false,
  p90Settings: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS
  },
  showP90: false,
  pdenDataSourceSetting: {
    source: EvaSource.Public,
    timeStep: TimeStep.Month
  },
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  survivorBias: true,
  rateType: 0,
  showIndividualSeries: false,
  cutoff: 50,
  timeStep: TimeStep.Month,
  shutInMonths: 3,
  source: EvaSource.Public,
  normalizeBy: DEFAULT_NORMALIZE_BY,
  useWeightedRatioAverage: true,
  xVariable: {
    valueProperty: "Header.ResourcePlay",
    displayTitle: "Resource Play",
    canBin: false,
    dataType: "Text",
    mappingName: "Header.ResourcePlay",
    bin: null
  },
  yVariable: {
    valueProperty: "Performance.Oil.TwelveMCalTimeCum.fcst",
    displayTitle: "12M CalTime Cum + Forecast (Mbbl)",
    canBin: true,
    dataType: "Number",
    mappingName: "Performance.Oil.TwelveMCalTimeCum.fcst"
  },
  lockUnits: false,
  stackedBarCalculation: CalculationMethod.Count,
  pressureChartSettings: {
    product: "Oil",
    productivityIndex: false
  },
  movingAverageDays: null,
  annotationSettings: {}
});

export const DEFAULT_BOX_PLOT_CHART_SETTINGS = createDefaultBoxPlotChartSettings();

export const createDefaultBoxPlotChartRequest = (): Partial<BoxPlotChartRequestType> => ({
  ...BASE_CHART_REQUEST,
  FilterId: "",
  requestId: "",
  ReverseColor: false,
  ColorPalette: {
    id: "-6",
    name: "Portland",
    shared: false,
    colors: ["rgb(12,51,131)"],
    preferredColors: [],
    paletteType: 0,
    colorScale: [],
    specialColors: null,
    thickness: [2],
    reverse: false
  },
  normalizeTypeWell: true,
  GroupBy: {
    title: "Resource Play",
    property: "Header.ResourcePlay",
    groupByField: "Header.ResourcePlay",
    pdenSource: "Public",
    canBin: false,
    dataType: "Text", // TODO: check this
    categoryId: 1,
    tooltip: "",
    display: "",
    entityKind: EntityKind.Well,
    bin: {
      BinSize: 0,
      MinSize: null,
      MaxBins: null,
      MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
      GreaterThan: null,
      LessThan: null,
      Quantile: {
        quantileType: "NumberOfQuantile",
        numQuantiles: 4
      },
      BinType: "BinSize",
      UseDynamicBins: false
    },
    mode: undefined,
    // Declared so createMergeWithDefaults keeps it: getAllowedKeys builds its allow-list from
    // this default object, and any key absent here is silently dropped from the request. Without
    // it a chart on Forecast Trumping sent a trumping-qualified property with no settings, and
    // the backend only runs the per-well source fallthrough when the settings are present.
    forecastTrumpingSettings: undefined,
    performanceDateRange: undefined
  },
  sortBy: "AlphabeticalAsc",
  userForecast: [],
  userForecastSettings: {
    forecastSourceFolder: null,
    excludeMcDanielForecasts: false,
    reserveCategory: null,
    excludeNoviForecasts: true,
    specialForecastFolders: ["McDaniel Research"]
  },
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  showGroupsNotInFilter: true,
  placeholderBinsEnabled: false,
  hasCheckedBins: true,
  syncClientPden: false,
  syncClientSchema: "",
  colorLockedItems: {},
  syncClientFcst: false,
  syncForecastRescat: "2111",
  normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
  chartSetting: createDefaultBoxPlotChartSettings() as BoxPlotChartSettings,
  chartFieldInformation: {
    groupByField: {
      property: "Header.ResourcePlay",
      title: "Resource Play",
      type: "groupBy",
      defaultUnit: "",
      folderName: "",
      isForecast: false,
      product: "",
      performanceDateRange: undefined
    },
    xField: {
      property: "Header.ResourcePlay",
      title: "Resource Play",
      type: "x",
      defaultUnit: "",
      product: "",
      folderName: "",
      isForecast: false,
      pdenSource: PdenSourceEnum.Public,
      performanceDateRange: undefined
    },
    yField: {
      property: "Performance.Oil.TwelveMCalTimeCum.fcst",
      title: "12M CalTime Cum + Forecast (Mbbl",
      type: "y",
      defaultUnit: "Mbbl",
      product: "Oil",
      folderName: "",
      isForecast: false,
      pdenSource: PdenSourceEnum.Public,
      performanceDateRange: undefined
    }
  },
  axisMinMax: {
    xMin: null,
    xMax: null,
    yMin: null,
    yMax: null
  },
  isDefaultLockOn: true,
  showScatter: false
});

export const DEFAULT_BOX_PLOT_CHART_REQUEST = createDefaultBoxPlotChartRequest();

export const generateBoxPlotRequestBody = createMergeWithDefaults(
  DEFAULT_BOX_PLOT_CHART_REQUEST
);
